import { Plugin, Widget, toWidget } from 'ckeditor5';

/**
 * Renders old Wiris/MathType `<math>...</math>` MathML as a real, clickable
 * widget instead of just letting it pass through as inert markup.
 *
 * WHY NOT NATIVE BROWSER MATHML: Chrome/Edge's native MathML ("MathML Core")
 * dropped several legacy elements Wiris commonly emits -- mfenced, menclose,
 * maligngroup/malignmark, full mmultiscripts -- so a lot of old equations
 * rendered blank or broken even though the saved <math> markup was fine.
 * MathJax's mml-chtml input processor still supports all of that, matching
 * how old Wiris equations already render correctly elsewhere in the app
 * (see src/Utils/utils.js, which wraps saved question HTML in MathJax).
 *
 * WHY A WIDGET: TemplatePanel.js listens for widget selection to let users
 * click an equation and reopen the editor modal. A bare passthrough <math>
 * tag was never selectable, so old equations couldn't be edited at all.
 *
 * WHAT GETS SAVED: dataDowncast re-emits the ORIGINAL <math> markup
 * byte-for-byte (captured on upcast), not MathJax's rendered output. This
 * keeps every question's saved HTML unchanged unless a user explicitly
 * clicks an old equation and converts it to the new KaTeX widget via the
 * modal (see TemplatePanel.js) -- nothing here silently rewrites data.
 */

let mathJaxPromise = null;

function loadMathJax() {
  if (window.MathJax?.startup?.promise) return window.MathJax.startup.promise;
  if (mathJaxPromise) return mathJaxPromise;

  mathJaxPromise = new Promise((resolve) => {
    window.MathJax = {
      loader: { load: ['[tex]/mhchem'] },
      tex: { packages: { '[+]': ['mhchem'] } },
      startup: {
        ready: () => {
          window.MathJax.startup.defaultReady();
          window.MathJax.startup.promise.then(resolve);
        },
      },
    };
    const script = document.createElement('script');
    // "tex-mml-chtml" is the combined component that understands BOTH our
    // \(latex\) equations elsewhere in the app AND raw <math> MathML input.
    script.src = 'https://cdn.jsdelivr.net/npm/mathjax@3/es5/tex-mml-chtml.js';
    document.head.appendChild(script);
  });
  return mathJaxPromise;
}

function renderMathJaxInto(domElement, mathml) {
  domElement.innerHTML = mathml;
  loadMathJax().then(() => {
    if (window.MathJax?.typesetPromise) {
      window.MathJax.typesetPromise([domElement]).catch((err) =>
        console.error('Legacy MathML render error:', err)
      );
    }
  });
}

// Serializes a CKEditor upcast view element (and its children) back to its
// original "<math ...>...</math>" HTML string, so it can be stored verbatim
// on the model element and re-emitted unchanged on save.
function stringifyViewElement(viewElement) {
  const container = document.createElement('div');
  container.appendChild(viewElementToDom(viewElement));
  return container.innerHTML;
}

function viewElementToDom(viewElement) {
  const el = document.createElementNS('http://www.w3.org/1998/Math/MathML', viewElement.name);
  for (const key of viewElement.getAttributeKeys()) {
    el.setAttribute(key, viewElement.getAttribute(key));
  }
  for (const child of viewElement.getChildren()) {
    if (child.is && child.is('$text')) {
      el.appendChild(document.createTextNode(child.data));
    } else if (child.is && child.is('element')) {
      el.appendChild(viewElementToDom(child));
    }
  }
  return el;
}

// Parses a stored "<math ...>...</math>" string back into its tag attributes
// + inner markup, so dataDowncast can rebuild the exact original element.
function parseMathML(mathmlString) {
  const container = document.createElement('div');
  container.innerHTML = mathmlString;
  const mathEl = container.querySelector('math');
  if (!mathEl) return { attributes: {}, innerHTML: '' };

  const attributes = {};
  for (const attr of mathEl.attributes) attributes[attr.name] = attr.value;
  return { attributes, innerHTML: mathEl.innerHTML };
}

export default class LegacyMathMLSupport extends Plugin {
  static get requires() {
    return [Widget];
  }

  static get pluginName() {
    return 'LegacyMathMLSupport';
  }

  init() {
    this._defineSchema();
    this._defineConverters();
  }

  _defineSchema() {
    this.editor.model.schema.register('legacyMathEquation', {
      inheritAllFrom: '$inlineObject',
      allowAttributes: ['mathml'],
    });
  }

  _defineConverters() {
    const editor = this.editor;
    const conversion = editor.conversion;

    // 'high' priority so this runs before General HTML Support's generic
    // element handling gets a chance to touch <math> at all.
    conversion.for('upcast').elementToElement({
      view: { name: 'math' },
      model: (viewElement, { writer }) => {
        const mathml = stringifyViewElement(viewElement);
        return writer.createElement('legacyMathEquation', { mathml });
      },
      converterPriority: 'high',
    });

    conversion.for('editingDowncast').elementToElement({
      model: 'legacyMathEquation',
      view: (modelElement, { writer }) => {
        const mathml = modelElement.getAttribute('mathml') || '';
        const span = writer.createContainerElement('span', { class: 'legacy-mathml-wrap' }, [
          writer.createRawElement('span', {}, (domEl) => renderMathJaxInto(domEl, mathml)),
        ]);
        return toWidget(span, writer, { label: 'Legacy equation (click to convert)' });
      },
    });

    conversion.for('dataDowncast').elementToElement({
      model: 'legacyMathEquation',
      view: (modelElement, { writer }) => {
        const mathml = modelElement.getAttribute('mathml') || '';
        const { attributes, innerHTML } = parseMathML(mathml);
        return writer.createRawElement('math', attributes, (domEl) => {
          domEl.innerHTML = innerHTML;
        });
      },
    });
  }
}
